import { useCallback, useEffect, useState } from "react";

import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";

import {
  Button,
  classes,
  Modal,
  Text,
  toastError,
  toastSuccess,
  useDisclosure,
} from "@alpacahq/ui/old";
import {
  Table,
  type TableColumn,
  type TableFilter,
  useDataGrid,
} from "@alpacahq/ui/old/datagrid";

import {
  getLiquidationErrorMessage,
  invalidateLiquidationQueries,
  useLock,
} from "~/v2/api/hooks";
import { liquidatePosition, type Position } from "~/v2/api/rest/account";
import { SymbolLink } from "~/v2/components/SymbolLink";
import { titleCase } from "~/v2/helpers";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { getCryptoBadge } from "~/v2/utils/display";
import {
  getNumericSortValue,
  getPrettyCash,
  getPrettyPercent,
  getPrettyQty,
  getProfitLossColor,
} from "~/v2/utils/formatting";
import { Radio } from "../entityOnboarding/Radio";

const defaultPageSize = 10;
const columns: readonly TableColumn<Position>[] = [
  {
    name: "Asset",
    key: "symbol",
    cellRenderer: ({ symbol, asset_class }) => (
      <SymbolLink symbol={symbol}>{getCryptoBadge(asset_class)}</SymbolLink>
    ),
  },
  {
    name: "Price",
    key: "current_price",
    alignRight: true,
    sortValue: ({ current_price }) => getNumericSortValue(current_price),
    cellRenderer: ({ current_price }) => (
      <Text>{getPrettyCash(current_price, { minimumFractionDigits: 2 })}</Text>
    ),
  },
  {
    name: "Qty",
    key: "qty",
    alignRight: true,
    sortValue: ({ qty }) => getNumericSortValue(qty),
    cellRenderer: ({ qty }) => <Text>{getPrettyQty(qty)}</Text>,
  },
  {
    name: "Side",
    key: "side",
    alignRight: true,
    cellRenderer: ({ side }) => titleCase(side ?? ""),
  },
  {
    name: "Market Value",
    key: "market_value",
    alignRight: true,
    sortValue: ({ market_value }) => getNumericSortValue(market_value),
    cellRenderer: ({ market_value }) => (
      <Text>{getPrettyCash(market_value, { minimumFractionDigits: 2 })}</Text>
    ),
  },
  {
    name: "Avg Entry",
    key: "avg_entry_price",
    alignRight: true,
    sortValue: ({ avg_entry_price }) => getNumericSortValue(avg_entry_price),
    cellRenderer: ({ avg_entry_price }) => (
      <Text>
        {getPrettyCash(avg_entry_price, {
          forcePrecision: 4,
          minimumFractionDigits: 2,
        })}
      </Text>
    ),
  },
  {
    name: "Cost Basis",
    key: "cost_basis",
    alignRight: true,
    sortValue: ({ cost_basis }) => getNumericSortValue(cost_basis),
    cellRenderer: ({ cost_basis }) => (
      <Text>
        {getPrettyCash(cost_basis, {
          forcePrecision: 4,
          minimumFractionDigits: 2,
        })}
      </Text>
    ),
  },
  {
    name: "Today's P/L (%)",
    key: "unrealized_intraday_plpc",
    alignRight: true,
    sortValue: ({ unrealized_intraday_plpc }) =>
      getNumericSortValue(unrealized_intraday_plpc),
    cellRenderer: ({ unrealized_intraday_plpc }) => (
      <Text className={classes(getProfitLossColor(unrealized_intraday_plpc))}>
        {getPrettyPercent(unrealized_intraday_plpc, false, true)}
      </Text>
    ),
  },
  {
    name: "Today's P/L ($)",
    key: "unrealized_intraday_pl",
    alignRight: true,
    sortValue: ({ unrealized_intraday_pl }) =>
      getNumericSortValue(unrealized_intraday_pl),
    cellRenderer: ({ unrealized_intraday_pl }) => (
      <Text className={classes(getProfitLossColor(unrealized_intraday_pl))}>
        {getPrettyCash(unrealized_intraday_pl, { minimumFractionDigits: 2 })}
      </Text>
    ),
  },
  {
    name: "Total P/L (%)",
    key: "unrealized_plpc",
    alignRight: true,
    sortValue: ({ unrealized_plpc }) => getNumericSortValue(unrealized_plpc),
    cellRenderer: ({ unrealized_plpc }) => (
      <Text className={classes(getProfitLossColor(unrealized_plpc))}>
        {getPrettyPercent(unrealized_plpc, false, true)}
      </Text>
    ),
  },
  {
    name: "Total P/L ($)",
    key: "unrealized_pl",
    alignRight: true,
    sortValue: ({ unrealized_pl }) => getNumericSortValue(unrealized_pl),
    cellRenderer: ({ unrealized_pl }) => {
      const num = Number(unrealized_pl || 0);

      return (
        <Text className={classes(getProfitLossColor(unrealized_pl))}>
          {getPrettyCash(num, { minimumFractionDigits: 2, withPrefix: true })}
        </Text>
      );
    },
  },
];

const filters: TableFilter<Position>[] = [
  {
    key: "assetClassFilter",
    label: "Asset Class",
    options: [
      { label: "Stocks", value: "us_equity" },
      { label: "Options", value: "us_option" },
      { label: "Crypto", value: "crypto" },
    ],
    filterFn: (row, filterValue) =>
      (Array.isArray(filterValue) ? filterValue : [filterValue]).some(
        (value) => value === row.asset_class,
      ),
  },
];

interface Props {
  positions: Position[];
  persistenceKey: string;
  searchValue: string;
  onSearchValueChange: (value: string) => void;
}

export const PositionsTable = ({
  persistenceKey,
  positions,
  searchValue,
  onSearchValueChange,
}: Props) => {
  const { currentAccount, product } = useAccountContext();
  const lock = useLock();
  const disclosure = useDisclosure();
  const client = useQueryClient();

  const tableProps = useDataGrid({
    rows: positions,
    columns,
    columnVisibility: true,
    pagination: {
      defaultPageSize,
    },
    persistence: { key: persistenceKey },
    filters: filters,
    search: {
      searchPlaceholder: "Search by symbol...",
      searchColumns: ["symbol"],
      onSearchValueChange,
    },
    rowSelection: {
      rowSelectionKey: "asset_id",
    },
    sort: true,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: searchValue is the trigger that pushes the local search term up to the table. tableProps.search is a prop object rebuilt every render, and the effect calls onSearchValueChange, so listing it would loop through the parent.
  useEffect(() => {
    if (tableProps.search?.searchValue !== searchValue) {
      tableProps.search?.onSearchValueChange(searchValue);
    }
  }, [searchValue]);

  const [cancelOrders, setCancelOrders] = useState(false);

  const { selectedRows = new Set<Position>() } = tableProps.rowSelection || {};

  const getSelectedPositionsString = useCallback(
    () =>
      // map selected indexes to positions
      // then build comma delimited string of first 10 positions
      positions
        .filter(({ symbol }) =>
          Array.from(selectedRows).find((s) => s.symbol === symbol),
        )
        .slice(0, 10)
        .map((position) => position.symbol)
        .join(", ")
        .concat(selectedRows.size > 10 ? "..." : ""),
    [selectedRows, positions],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: onClose is declared below this callback, so listing it is a TDZ error. lock.lock/unlock and the query client are stable but lock.locked is state the lock itself guards against re-entry, and listing it would give every consumer of onConfirm a new identity whenever the lock flips.
  const onConfirm = useCallback(async () => {
    try {
      // do nothing if already locked or if no positions are selected
      if (lock.locked || !selectedRows.size) {
        return;
      }

      // prevent multiple clicks
      lock.lock();

      // persist last symbol for error handling
      let lastSymbol = "";
      for (const position of Array.from(selectedRows)) {
        lastSymbol = position.symbol;

        try {
          // attempt to close position
          await liquidatePosition({
            accountID: currentAccount?.id || "",
            product: product,
            symbol: position.asset_id,
            cancelOrders: cancelOrders,
          });

          // show success toast
          toastSuccess(
            `Successfully liquidated ${selectedRows.size} positions.`,
            {},
          );
        } catch (error) {
          Sentry.captureException(error);

          toastError(
            `Error liquidating ${lastSymbol}. ${getLiquidationErrorMessage(error as Error, { cancelOrders }) || "unknown"}`,
            {},
          );
        }
      }
    } finally {
      // Refetch remaining positions and related order/balance/option data.
      invalidateLiquidationQueries(client);

      // dismiss modal
      onClose();

      // ready for next click
      lock.unlock();
    }
  }, [selectedRows, currentAccount, cancelOrders, product]);

  const onOpen = () => {
    document.getElementById("root")?.classList.add("blur");
    disclosure.onOpen();
  };

  const onClose = () => {
    document.getElementById("root")?.classList.remove("blur");
    disclosure.onClose();
  };

  const onChange = (isChecked: boolean) => setCancelOrders(isChecked);

  return (
    <>
      <Table
        {...tableProps}
        emptyMessage="No open positions. Place some trades to see this table populate"
        tools={[
          <Button
            key="liquidate-selected"
            onClick={onOpen}
            disabled={selectedRows.size === 0 || lock.locked}
          >
            Liquidate {selectedRows.size} selected
          </Button>,
        ]}
      />

      <Modal
        isOpen={disclosure.isOpen}
        onClose={onClose}
        title="Warning"
        controls={
          <div className="flex items-center justify-between gap-4">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={onConfirm}
              disabled={selectedRows.size === 0 || lock.locked}
              variant="red"
            >
              Confirm
            </Button>
          </div>
        }
      >
        <Text>
          Are you sure you want to liquidate{" "}
          <Text className="inline-block font-medium">{selectedRows.size}</Text>{" "}
          position(s)? This action cannot be undone.
          <br />
          <br />
          <Text className="font-medium">{getSelectedPositionsString()}</Text>
        </Text>
        <br />
        <Radio
          label={` Cancel all open orders for this symbol?`}
          name="liquidate_positions"
          isChecked={cancelOrders}
          onChange={onChange}
        />
      </Modal>
    </>
  );
};
